'use client';

import { useState } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from '../ui/carousel';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '../ui/button';

import { useLocale } from 'next-intl';
import { BasicProductDataResponse } from '@/types/catalog';
import { ProductCard } from '@/app/[locale]/stores/[storeId]/product/components/ProductCard';
import { Link } from '@/i18n/routing';
import { Routes } from '@/lib/routes/routes';

type CarouselSection = {
  slides: BasicProductDataResponse[];
  title?: string;
  englishName: string;
  arabicName: string;
};

function ProductsCarousel(props: CarouselSection) {
  const currentLang = useLocale();
  const branchId = props.slides?.[0]?.branchId ?? '';

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  return (
    <div className="h-full w-full flex-col overflow-hidden lg:flex">
      <Carousel
        setApi={setCarouselApi}
        opts={{
          slidesToScroll: 'auto',
          dragFree: true,
          direction: currentLang === 'ar' ? 'rtl' : 'ltr',
        }}
        className="m-auto w-full self-center lg:h-full"
      >
        <div className="flex justify-between">
          <div className="flex flex-col items-start pl-1">
            {props.title && <h1 className="mb-2 text-2xl font-semibold">{props.title}</h1>}

            <h2 className="mb-1 text-xl font-bold md:text-3xl">
              {currentLang === 'ar' ? props.arabicName : props.englishName}
            </h2>
          </div>
          <div className="mx-4 mb-2 flex justify-end gap-3 rtl:flex-row-reverse">
            <button
              className="bg-secondary grid h-9 w-9 place-items-center overflow-hidden rounded-full lg:h-12 lg:w-12"
              aria-label="Scroll to previous item" // Accessible name
              onClick={() => carouselApi?.scrollPrev()}
            >
              <ChevronLeftIcon className="text-primary" size={26} />
            </button>
            <button
              className="bg-secondary grid h-9 w-9 place-items-center overflow-hidden rounded-full lg:h-12 lg:w-12"
              aria-label="Scroll to next item" // Accessible name
              onClick={() => carouselApi?.scrollNext()}
            >
              <ChevronRightIcon className="text-primary" size={26} />
            </button>
          </div>
        </div>

        <CarouselContent className="flex touch-pan-y gap-0.5 px-1 py-3 lg:gap-2">
          {props.slides?.map((item, index) => (
            <CarouselItem
              key={index}
              dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
              className="flex-[0_0_180px] shrink-0 md:flex-[0_0_200px] lg:flex-[0_0_280px]"
            >
              <ProductCard productDetails={item} scroll={true} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mx-auto mt-8 w-full md:w-56">
          <Link href={Routes.branchDetails(branchId)}>
            <Button variant={'secondary'} className="py-4" rounded-sm={'full'}>
              {currentLang === 'ar' ? 'عرض المتجر' : 'Visit Store'}
            </Button>
          </Link>
        </div>
      </Carousel>
    </div>
  );
}

export default ProductsCarousel;
